/* eslint-disable no-nested-ternary */
import { Bars3Icon } from '@heroicons/react/24/solid';
import { createRef, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import t from 'typy';
import { CSSTransition } from 'react-transition-group';
import IsniError from '../../errors/IsniError';
import { useFetchISNI } from '../../fetchers/isni';
import { useFetchProfile } from '../../fetchers/profile';
import ClickOutside from '../../helpers/ClickOutside';
import { firstLetter } from '../../utils';
import IsniSkeletonLoader from '../loaders/skeletonloaders/isniskeletonloader';
import MobileMenuDropdown from '../mobilemenudropdown';
import NotificationDropdown from '../notificationdropdown';
import ProfileDropDown from '../profiledropdown';
import KampLogo from '../../assets/logo/logo.jpg';
import { BellIconSVG } from '../svgs';

const Navigation = () => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const [isMobileMenuDropdownOpen, setIsMobileMenuDropdownOpen] =
    useState(false);
  const headerRef = useRef(null);
  const nodeRef = createRef();
  const { data: profile } = useFetchProfile();
  const {
    data: isni,
    isLoading: isLoadingISNI,
    isError: isErrorISNI,
  } = useFetchISNI();
  const unReadNotifications = [];
  ClickOutside(headerRef, () => {
    setIsProfileDropdownOpen(false);
    setIsNotificationDropdownOpen(false);
    setIsMobileMenuDropdownOpen(false);
  });
  const handleProfileClick = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setIsNotificationDropdownOpen(false);
    setIsMobileMenuDropdownOpen(false);
  };
  const handleNotificationsClick = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
    setIsProfileDropdownOpen(false);
    setIsMobileMenuDropdownOpen(false);
  };
  const handleMobileMenuClick = () => {
    setIsMobileMenuDropdownOpen(!isMobileMenuDropdownOpen);
    setIsNotificationDropdownOpen(false);
    setIsProfileDropdownOpen(false);
  };
  return (
    <nav
      ref={headerRef}
      className="flex justify-between items-center px-4 lg:px-6 py-2.5"
    >
      <Link to="/dashboard" className="items-center hidden md:flex">
        <img src={KampLogo} className="h-10 mr-3" alt="Kamp Logo" />
      </Link>
      <div className="flex items-center md:hidden">
        <Bars3Icon
          className="w-8 h-8 text-black"
          onClick={handleMobileMenuClick}
        />
        <CSSTransition
          nodeRef={nodeRef}
          in={isMobileMenuDropdownOpen}
          timeout={300}
          classNames="mobile-menu"
          unmountOnExit
        >
          <div
            ref={nodeRef}
            className="top-0 bottom-0 left-0 block overflow-y-auto w-[18rem] bg-gray-700 shadow fixed h-full z-50"
          >
            <MobileMenuDropdown
              isOpen={isMobileMenuDropdownOpen}
              setIsOpen={setIsMobileMenuDropdownOpen}
            />
          </div>
        </CSSTransition>
      </div>
      <div className="flex items-center space-x-6 lg:order-2">
        <div className="hidden md:flex items-center justify-center space-x-1.5 text-center text-base text-gray-700">
          <p className="font-bold">ISNI</p>
          {isErrorISNI && <IsniError />}
          {isLoadingISNI ? (
            <IsniSkeletonLoader />
          ) : !isni?.isni_number || isni?.isni_number === '0000000000000000' ? (
            <span className="inline-block py-1 px-2.5 font-bold leading-none whitespace-nowrap align-baseline bg-yellow-200 rounded-full">
              pending
            </span>
          ) : (
            isni?.isni_number && (
              <span className="inline-block py-1 px-2.5 font-bold leading-none whitespace-nowrap align-baseline bg-blue-100 rounded-full">
                {isni?.isni_number}
              </span>
            )
          )}
        </div>
        <div className="flex items-center justify-center space-x-6">
          <div className="relative flex items-center justify-center">
            <button
              type="button"
              onClick={handleNotificationsClick}
              className="flex items-center justify-center w-10 h-10 overflow-hidden text-sm bg-gray-300 rounded-full hover:bg-gray-400"
            >
              {BellIconSVG}
              {unReadNotifications?.count > 0 && (
                <div className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 border-2 border-white rounded-full -top-2 -right-2">
                  {unReadNotifications?.count}
                </div>
              )}
            </button>
            <CSSTransition
              nodeRef={nodeRef}
              in={isNotificationDropdownOpen}
              timeout={300}
              classNames="dropdown"
              unmountOnExit
            >
              <div
                ref={nodeRef}
                className="z-50 absolute right-[-3.5rem] top-14 block bg-white divide-y divide-gray-100 rounded-lg shadow w-[20rem] max-h-[28rem] overflow-y-auto"
              >
                <NotificationDropdown
                  unReadNotifications={unReadNotifications?.data}
                />
              </div>
            </CSSTransition>
          </div>
          <div className="relative">
            <button
              onClick={handleProfileClick}
              type="button"
              className="flex items-center justify-center w-10 h-10 overflow-hidden text-sm border-0 rounded-full"
            >
              {t(profile?.profile_pic_url).isNullOrUndefined ||
              profile?.profile_pic_url === '' ? (
                <div className="inline-flex items-center justify-center w-full h-full bg-gray-300 rounded-full hover:bg-gray-400">
                  <span className="text-sm font-semibold text-gray-600">
                    {profile?.register_as === 'corporate'
                      ? firstLetter(profile?.corporation_name).toUpperCase()
                      : firstLetter(profile?.given_name).toUpperCase() +
                        firstLetter(profile?.other_names).toUpperCase()}
                  </span>
                </div>
              ) : (
                <img
                  className="w-10 h-10 rounded-full"
                  src={profile?.profile_pic_url}
                  loading="lazy"
                  alt={profile?.given_name || profile?.corporation_name}
                />
              )}
            </button>
            <CSSTransition
              nodeRef={nodeRef}
              in={isProfileDropdownOpen}
              timeout={300}
              classNames="dropdown"
              unmountOnExit
            >
              <div
                ref={nodeRef}
                className="z-50 my-4 w-[14rem] text-base list-none bg-white rounded divide-y divide-gray-100 shadow block absolute right-[0.5rem] top-10"
              >
                <ProfileDropDown
                  profile={profile}
                  isOpen={isProfileDropdownOpen}
                  setIsOpen={setIsProfileDropdownOpen}
                />
              </div>
            </CSSTransition>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
